import { noop } from 'rxjs';
import { sanitizeHtml, SanitizeHtmlOptions } from '../../helpers/sanitizeHtml';
import { MemoryService } from '../base/memoryService';
import { NULL_SERVICE, Service, ServiceLike } from '../base/service';

export interface HtmlService extends Service {
  readonly cleanHtml: string;
  /** @deprecated This HTML has been sanitized, but use #cleanHtml instead for clarity. */
  readonly html: string;

  setDirtyHtml(html: string): void;
  setCleanHtml(html: string): void;

  /** Calls the provided callback when changes are guaranteed to have been processed and emitted. */
  waitForChanges: HtmlServiceWaitForChangesFunction;
}

export type HtmlServiceWaitForChangesFunction = (onDone: () => void) => void;

export type MemoryHtmlServiceOptions = {
  sanitizeHtmlOptions?: SanitizeHtmlOptions;
};

export const NULL_HTML_SERVICE: HtmlService = {
  ...NULL_SERVICE,
  cleanHtml: '',
  html: '',
  setDirtyHtml: noop,
  setCleanHtml: noop,
  waitForChanges: onDone => {
    onDone();
  },
};

export function createMemoryHtmlService(options: MemoryHtmlServiceOptions = {}): HtmlService {
  return new MemoryHtmlService(options);
}

export class MemoryHtmlService extends MemoryService<string> implements HtmlService {
  constructor(options: MemoryHtmlServiceOptions = {}, ...dependencies: ServiceLike[]) {
    const { sanitizeHtmlOptions } = options;

    super('', ...dependencies);

    this.sanitizeHtml = dirtyHtml => sanitizeHtml(dirtyHtml, sanitizeHtmlOptions);
  }

  private readonly sanitizeHtml: (html: string) => string;

  get cleanHtml() {
    return this.state;
  }

  get html() {
    return this.cleanHtml;
  }

  setState(newState: string) {
    // Assume the HTML is dirty.
    this.setDirtyHtml(newState);
  }

  setDirtyHtml(dirtyHtml: string) {
    this.setCleanHtml(this.sanitizeHtml(dirtyHtml));
  }

  setCleanHtml(html: string) {
    super.setState(html);
  }

  // eslint-disable-next-line class-methods-use-this
  waitForChanges(onDone: () => void) {
    // There is no delay between making a change and changes$ emitting.
    onDone();
  }
}
